// The Security card (D-133). Frame from original/src/App.tsx:14262-14282 (the old "رموز الحماية" card);
// row / label / input / button classes from its "operations code" and "super code" rows (:14433-14640):
// section label `text-xs font-black ... uppercase tracking-widest flex items-center gap-2`, two-column grid,
// password input with the Eye toggle, full-width save button. The old contents (operations code, super code,
// recovery WhatsApp number, :14284-14800) are replaced by the six D-133 rows: change own PIN, reset PIN /
// password for others (D-037), 2FA (D-036), idle timeout (D-039), device approval (D-040), working hours (D-041).
// See DEVIATIONS DEV-03. No state except the show/hide toggles.
import { useState } from 'react';
import { Clock, Edit, Eye, EyeOff, KeyRound, Mail, Monitor, RefreshCw, Save, ShieldCheck, Users } from 'lucide-react';
import { useT } from '../i18n-context.js';
import { SettingsCardFrame } from './Settings.js';

export interface SecurityCardProps {
  readonly customIcon?: string;
  readonly changePin: {
    readonly currentPin: string;
    readonly newPin: string;
    readonly confirmPin: string;
    readonly onChange: (field: 'currentPin' | 'newPin' | 'confirmPin', value: string) => void;
    readonly onSave: () => void;
    readonly busy?: boolean;
  };
  /** `null` when the viewer lacks `manage_users`. */
  readonly resetOthers: {
    readonly users: readonly { readonly id: string; readonly label: string }[];
    readonly selectedUserId: string;
    readonly onSelectUser: (id: string) => void;
    readonly newPin: string;
    readonly onNewPinChange: (value: string) => void;
    readonly onResetPin: () => void;
    readonly onResetAccess: () => void;
    readonly busy?: boolean;
  } | null;
  readonly twoFactor: {
    readonly enrolled: boolean;
    readonly requiredForAll: boolean;
    /** Super Manager only (D-207); undefined = read-only. */
    readonly onRequiredForAllChange?: (value: boolean) => void;
    readonly onReEnrol: () => void;
  };
  readonly idleTimeout: {
    readonly minutes: number;
    readonly onChange: (minutes: number) => void;
    readonly onSave: () => void;
    readonly editable: boolean;
  };
  readonly deviceApproval: {
    readonly enabled: boolean;
    /** Super Manager only (D-040); undefined = read-only. */
    readonly onChange?: (value: boolean) => void;
  };
  /** [C35] D-049 e-mail channel (new section, V-98): the caller's own alert address, confirmed by a code sent to it. */
  readonly email?: {
    readonly address: string | null;
    readonly verified: boolean;
    readonly configured: boolean;
    readonly input: string;
    readonly onInput: (v: string) => void;
    readonly code: string;
    readonly onCode: (v: string) => void;
    readonly awaitingCode: boolean;
    readonly onSave: () => void;
    readonly onVerify: () => void;
    readonly onRemove: () => void;
    readonly busy?: boolean;
  };
  readonly workingHours: {
    readonly onManage?: () => void;
  };
}

const INPUT = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-lg dark:text-[#f0f6fc] transition-all disabled:opacity-60 disabled:cursor-not-allowed";
const SMALL_LABEL = "block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest";
const SECTION_LABEL = "block text-xs font-black mb-2 uppercase tracking-widest flex items-center gap-2";
const SUB = "text-[9px] text-gray-400 dark:text-[#8b949e] font-bold -mt-1";

function PinInput({ value, onChange, show, onToggle, invalid, testId }: { value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void; invalid?: boolean; testId: string }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        inputMode="numeric"
        maxLength={6}
        autoComplete="off"
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT} ${invalid ? "border-rose-500" : ""}`}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function Toggle({ on, onChange, testId }: { on: boolean; onChange?: ((v: boolean) => void) | undefined; testId: string }) {
  const t = useT();
  return (
    <button
      type="button"
      disabled={!onChange}
      data-testid={testId}
      onClick={() => onChange?.(!on)}
      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all disabled:cursor-not-allowed ${on ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50" : "bg-gray-50 dark:bg-[#05070a] text-gray-400 dark:text-[#8b949e] border-gray-200 dark:border-[#30363d]"}`}
    >
      {on ? t('security.on') : t('security.off')}
    </button>
  );
}

export function SecurityCard(props: SecurityCardProps) {
  const t = useT();
  const [showPin, setShowPin] = useState(false);
  const [showResetPin, setShowResetPin] = useState(false);
  const cp = props.changePin;
  const mismatch = cp.confirmPin !== "" && cp.confirmPin !== cp.newPin;
  const canSavePin = cp.currentPin.length === 6 && cp.newPin.length === 6 && cp.newPin === cp.confirmPin && !cp.busy;
  const ro = props.resetOthers;

  return (
    <SettingsCardFrame cardKey="security" {...(props.customIcon ? { customIcon: props.customIcon } : {})}>
      <div className="space-y-8">
        {/* 1. Change own PIN */}
        <div className="space-y-4">
          <label className={`${SECTION_LABEL} text-blue-600 dark:text-blue-400`}>
            <Edit size={14} />
            {t('security.change_pin')}
          </label>
          <p className={SUB}>{t('security.change_pin_sub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={SMALL_LABEL}>{t('security.current_pin')}</label>
              <PinInput value={cp.currentPin} onChange={(v) => cp.onChange('currentPin', v)} show={showPin} onToggle={() => setShowPin(!showPin)} testId="security-current-pin" />
            </div>
            <div>
              <label className={SMALL_LABEL}>{t('security.new_pin')}</label>
              <PinInput value={cp.newPin} onChange={(v) => cp.onChange('newPin', v)} show={showPin} onToggle={() => setShowPin(!showPin)} testId="security-new-pin" />
            </div>
            <div>
              <label className={SMALL_LABEL}>{t('security.confirm_pin')}</label>
              <PinInput value={cp.confirmPin} onChange={(v) => cp.onChange('confirmPin', v)} show={showPin} onToggle={() => setShowPin(!showPin)} invalid={mismatch} testId="security-confirm-pin" />
            </div>
          </div>
          <button
            onClick={cp.onSave}
            disabled={!canSavePin}
            data-testid="security-save-pin"
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Save size={16} />
            {t('security.save_pin')}
          </button>
        </div>

        {/* 2. Reset PIN / password for others (D-037) */}
        {ro && (
          <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]">
            <label className={`${SECTION_LABEL} text-rose-600 dark:text-rose-400`}>
              <RefreshCw size={14} />
              {t('security.reset_others')}
            </label>
            <p className={SUB}>{t('security.reset_others_sub')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={SMALL_LABEL}>{t('security.select_user')}</label>
                <select
                  value={ro.selectedUserId}
                  onChange={(e) => ro.onSelectUser(e.target.value)}
                  data-testid="security-reset-user"
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 dark:text-[#f0f6fc] transition-all font-black text-right"
                >
                  <option value="">{t('select.placeholder')}</option>
                  {ro.users.map((u) => (
                    <option key={u.id} value={u.id}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={SMALL_LABEL}>{t('security.new_pin')}</label>
                <PinInput value={ro.newPin} onChange={ro.onNewPinChange} show={showResetPin} onToggle={() => setShowResetPin(!showResetPin)} testId="security-reset-pin-value" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={ro.onResetPin}
                disabled={!ro.selectedUserId || ro.newPin.length !== 6 || ro.busy}
                data-testid="security-reset-pin"
                className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} />
                {t('security.reset_pin')}
              </button>
              <button
                onClick={ro.onResetAccess}
                disabled={!ro.selectedUserId || ro.busy}
                data-testid="security-reset-access"
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2"
              >
                <KeyRound size={16} />
                {t('security.reset_access')}
              </button>
            </div>
          </div>
        )}

        {/* 3. 2FA (D-036) */}
        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]">
          <label className={`${SECTION_LABEL} text-emerald-600 dark:text-emerald-400`}>
            <ShieldCheck size={14} />
            {t('security.two_factor')}
          </label>
          <p className={SUB}>{t('security.two_factor_sub')}</p>
          <div className="flex items-center justify-between p-4 bg-white dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl">
            <span className="text-xs font-bold text-gray-800 dark:text-[#f0f6fc]">
              {props.twoFactor.enrolled ? t('security.two_factor_enrolled') : t('security.two_factor_not_enrolled')}
            </span>
            <button
              type="button"
              onClick={props.twoFactor.onReEnrol}
              data-testid="security-reenrol"
              className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all"
            >
              {t('security.re_enrol')}
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-white dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl">
            <span className="text-xs font-bold text-gray-800 dark:text-[#f0f6fc]">{t('security.two_factor_required_all')}</span>
            <Toggle on={props.twoFactor.requiredForAll} onChange={props.twoFactor.onRequiredForAllChange} testId="security-2fa-all" />
          </div>
        </div>

        {/* 4. Idle timeout (D-039) */}
        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]">
          <label className={`${SECTION_LABEL} text-amber-600 dark:text-amber-400`}>
            <Clock size={14} />
            {t('security.idle_timeout')}
          </label>
          <p className={SUB}>{t('security.idle_timeout_sub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className={SMALL_LABEL}>{t('security.minutes')}</label>
              <input
                type="number"
                min={1}
                max={60}
                value={props.idleTimeout.minutes}
                disabled={!props.idleTimeout.editable}
                data-testid="security-idle-minutes"
                onChange={(e) => props.idleTimeout.onChange(parseInt(e.target.value || "0"))}
                className={INPUT}
              />
            </div>
            <button
              onClick={props.idleTimeout.onSave}
              disabled={!props.idleTimeout.editable || props.idleTimeout.minutes < 1 || props.idleTimeout.minutes > 60}
              data-testid="security-idle-save"
              className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <Save size={16} />
              {t('common.save')}
            </button>
          </div>
        </div>

        {/* 5. Device approval (D-040) */}
        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]">
          <label className={`${SECTION_LABEL} text-indigo-600 dark:text-indigo-400`}>
            <Monitor size={14} />
            {t('security.device_approval')}
          </label>
          <p className={SUB}>{t('security.device_approval_sub')}</p>
          <div className="flex items-center justify-between p-4 bg-white dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl">
            <span className="text-xs font-bold text-gray-800 dark:text-[#f0f6fc]">{t('security.device_approval')}</span>
            <Toggle on={props.deviceApproval.enabled} onChange={props.deviceApproval.onChange} testId="security-devices" />
          </div>
        </div>

        {/* [C35] D-049 e-mail for alerts (V-98) */}
        {props.email && (
          <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]" data-testid="security-email">
            <label className={`${SECTION_LABEL} text-sky-600 dark:text-sky-400`}>
              <Mail size={14} />
              {t('security.email')}
            </label>
            <p className={SUB}>{props.email.configured ? t('security.email_sub') : t('security.email_not_configured')}</p>
            {props.email.address && (
              <div className="flex items-center justify-between p-3 bg-white dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl text-xs font-bold">
                <span className="font-mono text-gray-800 dark:text-[#f0f6fc]" dir="ltr">{props.email.address}</span>
                <span className={props.email.verified ? 'text-emerald-600' : 'text-amber-600'} data-testid="security-email-state">{props.email.verified ? t('security.email_verified') : t('security.email_unverified')}</span>
              </div>
            )}
            <div className="flex gap-2">
              <input type="email" dir="ltr" value={props.email.input} onChange={(e) => props.email!.onInput(e.target.value)} placeholder="name@example.com" disabled={!props.email.configured} data-testid="security-email-input" className="flex-1 bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-xs font-mono dark:text-[#f0f6fc]" />
              <button type="button" onClick={props.email.onSave} disabled={!props.email.configured || props.email.busy || !props.email.input.trim()} data-testid="security-email-save" className="px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-black text-xs disabled:opacity-50">
                {t('security.email_send_code')}
              </button>
            </div>
            {props.email.awaitingCode && (
              <div className="flex gap-2">
                <input type="text" inputMode="numeric" maxLength={6} dir="ltr" value={props.email.code} onChange={(e) => props.email!.onCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" data-testid="security-email-code" className="flex-1 bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center text-sm font-mono tracking-[0.4em] dark:text-[#f0f6fc]" />
                <button type="button" onClick={props.email.onVerify} disabled={props.email.busy || props.email.code.length !== 6} data-testid="security-email-verify" className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs disabled:opacity-50">
                  {t('security.email_verify')}
                </button>
              </div>
            )}
            {props.email.address && (
              <button type="button" onClick={props.email.onRemove} disabled={props.email.busy} className="w-full py-1.5 text-[10px] font-bold text-rose-500 hover:text-rose-600 transition-colors">
                {t('security.email_remove')}
              </button>
            )}
          </div>
        )}

        {/* 6. Working hours (D-041) */}
        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-[#30363d]">
          <label className={`${SECTION_LABEL} text-gray-600 dark:text-[#8b949e]`}>
            <Users size={14} />
            {t('security.working_hours')}
          </label>
          <p className={SUB}>{t('security.working_hours_sub')}</p>
          <button
            type="button"
            onClick={props.workingHours.onManage}
            disabled={!props.workingHours.onManage}
            data-testid="security-working-hours"
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 dark:bg-[#161b22] dark:hover:bg-[#21262d] text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all text-xs disabled:opacity-60"
          >
            {t('security.working_hours_manage')}
          </button>
        </div>
      </div>
    </SettingsCardFrame>
  );
}
